<script lang="ts">
  // BJL-CHECK: Alert (used), Button (used), Card (used), Spinner (used). The sub-controls
  // document their own use (PlaceField → InputBare, DepartRow, TripOptions, ItineraryCard →
  // Card, DetailView, RideStep → Collapsible). See artifacts/deviations/PlanPage.md.
  //
  // Plan Trip: origin/destination pickers → itinerary list → step-by-step detail.
  // Search fires automatically once both ends are set; the chosen itinerary is drawn
  // by TransitMap (it subscribes to useTripPlanStore.activeItinerary).
  import { Alert } from '@byjackli-labs/ui/Alert'
  import { Button } from '@byjackli-labs/ui/Button'
  import { Card } from '@byjackli-labs/ui/Card'
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import type { Itinerary } from '../services/api'
  import { Colors } from '../ui/colors'
  import { Type } from '../ui/theme'
  import { useTripPlanStore } from '../store/tripPlan'
  import { useRiderStore } from '../store/rider'
  import { useGeolocation } from '../hooks/useGeolocation.svelte'
  import { useBackHandler } from '../hooks/useBackHandler.svelte'
  import { storeSelect } from '../lib/store.svelte'
  import PlaceField from './Plan/PlaceField.svelte'
  import DepartRow from './Plan/DepartRow.svelte'
  import TripOptions from './Plan/TripOptions.svelte'
  import ItineraryCard from './Plan/ItineraryCard.svelte'
  import DetailView from './Plan/DetailView.svelte'

  interface Props {
    onStartTrip?: () => void
  }

  const { onStartTrip }: Props = $props()

  const origin = storeSelect(useTripPlanStore, (s) => s.origin)
  const destination = storeSelect(useTripPlanStore, (s) => s.destination)
  const departAt = storeSelect(useTripPlanStore, (s) => s.departAt)
  const status = storeSelect(useTripPlanStore, (s) => s.status)
  const itineraries = storeSelect(useTripPlanStore, (s) => s.itineraries)
  const errorMessage = storeSelect(useTripPlanStore, (s) => s.errorMessage)
  const recentTrips = storeSelect(useTripPlanStore, (s) => s.recentTrips)
  const activeItinerary = storeSelect(useTripPlanStore, (s) => s.activeItinerary)

  const geo = useGeolocation()
  let detail = $state<Itinerary | null>(null)
  // Mode + depart-time controls live behind the ⋯ menu so the plan header stays two rows
  // of just origin/destination; the options are secondary to picking the two ends.
  let optionsOpen = $state(false)

  // Both ends picked → search. Also re-fires on swap and depart-at changes. The three
  // reads are the whole dependency set (the React version's dep array); `search` itself is
  // pulled off the store at call time so reading it can't re-trigger this.
  $effect(() => {
    const from = origin.value
    const to = destination.value
    departAt.value
    if (from && to) void useTripPlanStore.getState().search()
  })

  // Closing the sheet (this page is unmounted with it) hands the map back to the whole
  // system — the trip stays drawn, but every other line and vehicle is visible again.
  // A live ride session is the exception: focus is what makes the map readable while
  // riding, and "Start trip" deliberately closes the sheet to get out of the way.
  $effect(() => {
    return () => {
      if (useRiderStore.getState().phase === 'idle') useTripPlanStore.getState().setMapFocus(false)
    }
  })

  // The detail view shows an itinerary from the CURRENT results; new results drop it.
  $effect(() => {
    itineraries.value
    detail = null
  })

  // Back = "I'm done looking at this one": the itinerary stays drawn, but the map stops
  // hiding everything else so the rider can compare it to the system. Shared by the ‹ button
  // in DetailView and Android Back below.
  function closeDetail() {
    useTripPlanStore.getState().setMapFocus(false)
    detail = null
  }

  // Android Back drops the step-by-step detail back to the list of trip options, mirroring
  // DetailView's ‹. Registered while a trip is open, so it sits above App's sheet-close
  // handler on the stack — Back leaves the detail before it closes the Plan sheet.
  useBackHandler(() => detail !== null, closeDetail)

  const currentLocation = $derived(
    geo.location ? { latitude: geo.location.latitude, longitude: geo.location.longitude } : null,
  )
</script>

<div style="flex: 1; overflow-y: auto; padding: 12px 16px 24px;">
  {#if detail}
    <DetailView it={detail} onBack={closeDetail} onStart={onStartTrip} />
  {:else}
    <div style="display: flex; flex-direction: column; gap: 12px;">
      <!-- Origin / destination + swap -->
      <div style="display: flex; gap: 8px; align-items: stretch;">
        <div style="display: flex; flex-direction: column; gap: 8px; flex: 1; min-width: 0;">
          <PlaceField
            label="From"
            place={origin.value}
            onSelect={(p) => useTripPlanStore.getState().setOrigin(p)}
            {currentLocation}
          />
          <PlaceField
            label="To"
            place={destination.value}
            onSelect={(p) => useTripPlanStore.getState().setDestination(p)}
            {currentLocation}
          />
        </div>
        <div
          style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; flex-shrink: 0;"
        >
          <!-- Options (mode + depart time) menu, stacked above swap -->
          <div style="position: relative;">
            <Button
              variant={optionsOpen ? 'default' : 'secondary'}
              iconOnly
              onclick={() => { optionsOpen = !optionsOpen }}
              aria-label="Trip options"
              aria-expanded={optionsOpen}
              style="border-radius: 17px; position: relative; z-index: 45;"
            >⋯</Button>
            {#if optionsOpen}
              <!-- Full-screen catcher: a tap anywhere else closes the menu. -->
              <!-- svelte-ignore a11y_no_static_element_interactions -->
              <div
                onpointerdown={() => { optionsOpen = false }}
                style="position: fixed; inset: 0; z-index: 40;"
              ></div>
              <div
                style="
                  position: absolute; top: 100%; right: 0; margin-top: 6px; z-index: 50;
                  display: flex; flex-direction: column; align-items: stretch; gap: 10px;
                  padding: 12px; min-width: 176px;
                  background-color: {Colors.surfaceElevated};
                  border: 1px solid {Colors.borderStrong};
                  border-radius: var(--bjl-radius-md, 10px);
                  box-shadow: var(--bjl-shadow-lg, 0 8px 24px rgba(0,0,0,0.55));
                "
              >
                <TripOptions />
                <DepartRow />
              </div>
            {/if}
          </div>
          <Button
            variant="secondary"
            iconOnly
            onclick={() => useTripPlanStore.getState().swapEnds()}
            aria-label="Swap origin and destination"
            style="flex-shrink: 0; border-radius: 17px;"
          >⇅</Button>
        </div>
      </div>

      {#if status.value === 'loading'}
        <div
          style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 16px 0; color: {Colors.textSecondary}; {Type.body};"
        >
          <Spinner size="sm" label="Finding routes" /> Finding routes…
        </div>
      {/if}

      {#if status.value === 'error'}
        <Alert variant="error" size="sm">{errorMessage.value}</Alert>
      {/if}

      {#if status.value === 'results' && itineraries.value.length === 0}
        <div
          style="color: {Colors.textSecondary}; {Type.body}; padding: 16px 0; text-align: center;"
        >No route found between those points.</div>
      {/if}

      {#if status.value === 'results'}
        {#each itineraries.value as it, i (i)}
          <ItineraryCard
            {it}
            onPress={() => {
              useTripPlanStore.getState().setActiveItinerary(it)
              detail = it
            }}
          />
        {/each}
      {/if}

      <!-- Recents fill the idle screen -->
      {#if status.value === 'idle' && !activeItinerary.value && recentTrips.value.length > 0}
        <div style="display: flex; flex-direction: column; gap: 6px; margin-top: 4px;">
          <span
            style="display: flex; align-items: center; gap: 6px; color: {Colors.textMuted}; {Type.eyebrow};"
          >RECENT<span style="font-size: 13px;">🕘</span></span>
          {#each recentTrips.value as r, i (i)}
            <Card
              clickable
              ariaLabel="Plan {r.origin.name} to {r.destination.name} again"
              onclick={() => {
                const plan = useTripPlanStore.getState()
                plan.setOrigin(r.origin)
                plan.setDestination(r.destination)
              }}
            >
              <span style="display: flex; align-items: center; gap: 8px; text-align: left;">
                <span
                  style="
                    color: {Colors.text}; {Type.body};
                    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
                  "
                >
                  {r.origin.name} <span style="color: {Colors.textMuted}; font-size: 1.25em; font-weight: 900; -webkit-text-stroke: 0.6px currentColor;">→</span> {r.destination.name}
                </span>
              </span>
            </Card>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>
